<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "../../PhotoBox.vue";

export default {
    name: "OrgChartTemp7",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        orgChart: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            overlayImage: "",
            footerImage: ""
        };
    },
    computed: {
        memberPages() {
            const pages = [];
            const members = this.orgChart.members || [];
            for (let i = 0; i < members.length; i += 4) {
                pages.push(members.slice(i, i + 4));
            }
            return pages.length > 0 ? pages : [[]];
        },
        headerColor() {
            return this.orgChart && this.orgChart.style && this.orgChart.style.colorScheme && this.orgChart.style.colorScheme.body;
        }
    },
    watch: {
        orgChart: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.generateOverlayImage();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.generateGradients();
            this.generateOverlayImage();
        });
    },
    methods: {
        hexToRGBA(hex, opacity) {
            if (!hex) return `rgba(0,0,0,${opacity})`;
            let r = 0, g = 0, b = 0;
            const cleanHex = hex.replace('#', '');
            if (cleanHex.length === 3) {
                r = parseInt(cleanHex[0] + cleanHex[0], 16);
                g = parseInt(cleanHex[1] + cleanHex[1], 16);
                b = parseInt(cleanHex[2] + cleanHex[2], 16);
            } else if (cleanHex.length === 6) {
                r = parseInt(cleanHex.substring(0, 2), 16);
                g = parseInt(cleanHex.substring(2, 4), 16);
                b = parseInt(cleanHex.substring(4, 6), 16);
            }
            return `rgba(${r},${g},${b},${opacity})`;
        },
        generateOverlayImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 1150;
            canvas.height = 635;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = this.hexToRGBA(this.headerColor, 0.85);
            ctx.fillRect(0, 0, 1150, 635);
            this.overlayImage = canvas.toDataURL();
        },
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 1150;
            fCanvas.height = 300;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 300, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 1150, 300);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': orgChart.style.colorScheme && orgChart.style.colorScheme.header,
        '--body-color': orgChart.style.colorScheme && orgChart.style.colorScheme.body,
        '--footer-color': orgChart.style.colorScheme && orgChart.style.colorScheme.footer,
        '--heading-color': orgChart.style.heading.color,
        '--heading-size': orgChart.style.heading.size + 'px',
        '--heading-weight': orgChart.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': orgChart.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': orgChart.style.heading.underline ? 'underline' : 'none',
        '--heading-font': orgChart.style.heading.font,
        '--label-color': orgChart.style.label.color,
        '--label-size': orgChart.style.label.size + 'px',
        '--label-weight': orgChart.style.label.bold ? 'bold' : 'normal',
        '--label-style': orgChart.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': orgChart.style.label.underline ? 'underline' : 'none',
        '--label-font': orgChart.style.label.font,
        '--text-color': orgChart.style.text.color,
        '--text-size': orgChart.style.text.size + 'px',
        '--text-weight': orgChart.style.text.bold ? 'bold' : 'normal',
        '--text-style': orgChart.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': orgChart.style.text.underline ? 'underline' : 'none',
        '--text-font': orgChart.style.text.font,
    }">
        <div v-for="(pageMembers, pageIndex) in memberPages" :key="pageIndex" class="profile-container page-unit">

            <div v-if="orgChart.projects && orgChart.projects[pageIndex]">
                <div v-if="(orgChart.projects[pageIndex] && orgChart.projects[pageIndex].uploadedPhoto) || (orgChart.projects[pageIndex] && orgChart.projects[pageIndex].photoURL)">
                    <div class="project-img photobox">
                        <PhotoBox :project="orgChart.projects[pageIndex]" :bgColor="orgChart.style.colorScheme && orgChart.style.colorScheme.body"
                            position="top-center" class="w-full h-full" objectFit="cover" />
                    </div>
                </div>
            </div>
            <!-- Header -->
            <div class="header">
                <div class="logo-container photobox">
                    <PhotoBox :project="orgChart" type="logo" bgColor="transparent" objectFit="contain" />
                </div>
            </div>


            <!-- Content Area -->
            <div class="content-wrapper">
                <div class="full-page-overlay" v-if="overlayImage">
                    <img :src="overlayImage" class="w-full h-full" />
                </div>
                <div class="content-area">
                    <h1 class="page-title">
                        <SpellCheckedText :value="orgChart.title || ''" @input="orgChart.title = $event" />
                    </h1>
                    <div class="members-grid">
                        <div v-for="member in pageMembers" :key="member.teamMemberId" class="member-card">
                            <div class="image-container photobox">
                                <PhotoBox :project="member" type="profile" :bgColor="'#f0f0f0'" objectFit="cover" />
                            </div>
                            <div class="member-details">
                                <h2 class="member-name">
                                    <SpellCheckedText :value="member.teamMemberName || ''"
                                        @input="member.teamMemberName = $event" />
                                </h2>
                                <div class="role-section mb-3">
                                    <span class="label">Role on Project: </span>
                                    <span class="role-text">
                                        <SpellCheckedText :value="member.role || ''" @input="member.role = $event" />
                                    </span>
                                </div>
                                <div class="bio-section">
                                    <span class="label">Bio: </span>
                                    <span class="bio-text">
                                        <SpellCheckedText :value="member.whyTeamMember || ''"
                                            @input="member.whyTeamMember = $event" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="footer">
                <div class="footer-overlay" v-if="orgChart.projects && orgChart.projects[pageIndex] && footerImage">
                    <img :src="footerImage" class="w-full h-full" />
                </div>
                <div class="footer-left">
                    <SpellCheckedText :value="orgChart.opprName || ''" @input="orgChart.opprName = $event" />
                </div>
                <div class="footer-right">
                    <span class="company-name">
                        <SpellCheckedText :value="orgChart.submittedBy || ''" @input="orgChart.submittedBy = $event" />
                    </span>
                    <!-- <span class="divider">|</span> -->
                    <span class="page-number">{{ String(startPage + pageIndex).padStart(2, '0') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;

    .profile-container {
        position: relative;
        width: 297mm;
        height: 210mm;
        display: flex;
        flex-direction: column;
        background: white;
        box-sizing: border-box;
        overflow: hidden;
    }

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .project-img {
        width: 100%;
        height: 100%;
        z-index: 0;
        position: absolute;
        top: 0;
        left: 0;
    }

    .header {
        height: 80px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding: 0 20px;

        .logo-container {
            height: 70px;
            width: 180px;
        }
    }

    .content-wrapper {
        display: flex;
        flex: 1;
        position: relative;
        padding: 40px;

        .full-page-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            pointer-events: none;
        }

        .content-area {
            position: relative;
            display: flex;
            flex-direction: column;
            z-index: 2;

            .page-title {
                color: var(--header-color);
                font-size: 28px;
                font-family: Helvetica;
                font-weight: bold;
                margin-bottom: 40px;
            }

            .members-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 1fr 1fr;
                gap: 40px;
                width: 100%;
            }

            .member-card {
                display: flex;
                align-items: flex-start;
                gap: 20px;

                .image-container {
                    flex-shrink: 0;
                    width: 150px;
                    height: 180px;
                    background: #f0f0f0;
                    overflow: hidden;

                    :deep(img) {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    :deep(.photo-box-wrapper),
                    :deep(.image-container) {
                        width: 100%;
                        height: 100%;
                    }
                }

                .member-details {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    max-height: 225px;
                    overflow: hidden;

                    .member-name {
                        color: var(--heading-color);
                        font-size: var(--heading-size);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                    }

                    .role-section,
                    .bio-section {
                        .label {
                            color: var(--label-color);
                            font-size: var(--label-size);
                            font-family: var(--label-font);
                            font-weight: var(--label-weight);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                            margin: 0
                        }

                        .role-text,
                        .bio-text {
                            color: var(--text-color);
                            font-size: var(--text-size);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            margin: 0;
                        }

                        .bio-text {
                            overflow: hidden;
                        }
                    }
                }
            }
        }
    }

    .footer {
        height: 80px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 40px;
        font-size: 12px;
        padding-top: 10px;
        position: relative;
        z-index: 2;

        .footer-overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 2;
            pointer-events: none;
        }

        .footer-left {
            font-size: 12px;
            font-weight: bold;
            color: var(--footer-color);
            margin: 0;
        }

        .footer-right {
            display: flex;
            align-items: center;

            .company-name {
                font-size: 12px;
                font-weight: bold;
                color: var(--footer-color);
                margin: 0;
            }

            .divider {
                font-size: 12px;
                font-weight: bold;
                color: var(--footer-color);
                margin: 0;
                padding: 0px 10px;
            }

            .page-number {
                font-size: 16px;
                font-weight: 800;
                color: var(--header-color);
                margin: 0;
            }
        }
    }
}
</style>
